/**
 * The app's single responsive system (de-slop FE-16). Pure module (no
 * react-native import) so it can be unit-tested; `useResponsive` wraps it.
 *
 *   phone   < 760
 *   tablet  760–1179
 *   desktop 1180–1439
 *   wide    >= 1440
 *
 * 760 and 1180 are where the WebAppShell switches layouts. The same values are
 * mirrored in `tailwind.config.js` `theme.screens` (sm/md 760, lg 1180,
 * xl 1440) so NativeWind classes and JS agree; __tests__/responsive.test.ts
 * keeps them in sync.
 */
export const BREAKPOINTS = {
  tablet: 760,
  desktop: 1180,
  wide: 1440,
} as const;

export type Breakpoint = 'phone' | 'tablet' | 'desktop' | 'wide';

export interface ResponsiveInfo {
  width: number;
  height: number;
  bp: Breakpoint;
  isPhone: boolean;
  isTablet: boolean;
  /** desktop or wide (>= 1180). */
  isDesktop: boolean;
  isWide: boolean;
  /** phone or tablet (< 1180). */
  isCompact: boolean;
}

export function breakpointForWidth(width: number): Breakpoint {
  if (width < BREAKPOINTS.tablet) return 'phone';
  if (width < BREAKPOINTS.desktop) return 'tablet';
  if (width < BREAKPOINTS.wide) return 'desktop';
  return 'wide';
}

export function responsiveInfoFor(width: number, height: number): ResponsiveInfo {
  const bp = breakpointForWidth(width);
  return {
    width,
    height,
    bp,
    isPhone: bp === 'phone',
    isTablet: bp === 'tablet',
    isDesktop: bp === 'desktop' || bp === 'wide',
    isWide: bp === 'wide',
    isCompact: bp === 'phone' || bp === 'tablet',
  };
}
